'use client'

import { ReactNode } from 'react'
import { cn } from '@/lib/utils'

interface PageHeaderProps {
    /** Page title - displayed as H1. ReactNode allowed for detail pages (name + badges) */
    title: ReactNode
    /** Optional subtitle or description; ReactNode for detail-page secondary lines (links, dir spans) */
    subtitle?: ReactNode
    /** Optional element before the title stack, outside the h1 (detail-page avatar) */
    leading?: ReactNode
    /** Primary action button - anchored to top-right, first on mobile */
    primaryAction?: ReactNode
    /** Secondary action button(s) - left of primary on desktop, below it on mobile */
    secondaryActions?: ReactNode
    /** Toolbar row content (tabs, search, filters, sort) - renders in bottom row */
    toolbar?: ReactNode
    /** Additional content below title (e.g., stats, summary) */
    children?: ReactNode
    /** Optional className for the container */
    className?: string
}

/**
 * PageHeader - Unified header for all main pages
 * Figma: Etz Design System / Page Header & Toolbar / PageHeader (449:136).
 *
 * Anatomy (reskin 2026-07-31 — masthead, not a card):
 * - No container: the header sits directly on the canvas and closes with a
 *   single hairline rule (border/subtle). Structure comes from the rule and
 *   whitespace, not a box.
 * - Row 1: serif title stack with actions right; below sm the actions stack
 *   full-width with the primary action first
 * - Row 2 (optional): toolbar slot (segmented pill track + search) below the rule
 */
export function PageHeader({
    title,
    subtitle,
    leading,
    primaryAction,
    secondaryActions,
    toolbar,
    children,
    className
}: PageHeaderProps) {
    const hasActions = Boolean(primaryAction || secondaryActions)

    return (
        <div className={cn(className)}>
            {/* ROW 1: Title & Actions — masthead rule below. The location trail
                lives in the topbar (2026-08-04) — this header has no breadcrumb. */}
            <div className="pb-5 border-b border-[var(--border-subtle)]">
                {/* md: not sm: — the serif text-3xl title + a 3-action row
                    collide between 640-780px; stack until md */}
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="flex items-center gap-4 min-w-0">
                        {leading && <div className="shrink-0">{leading}</div>}
                        <div className="flex flex-col gap-1 min-w-0">
                            <h1 className="font-serif text-3xl font-medium text-[var(--text-primary)]">
                                {title}
                            </h1>
                            {subtitle && (
                                <div className="text-sm leading-5 text-[var(--text-tertiary)]">
                                    {subtitle}
                                </div>
                            )}
                        </div>
                    </div>

                    {hasActions && (
                        // flex-wrap + justify-end so a 4-action header wraps its
                        // buttons rather than crushing the title stack
                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end [&>*]:w-full sm:[&>*]:w-auto">
                            {secondaryActions}
                            {primaryAction}
                        </div>
                    )}
                </div>
            </div>

            {/* ROW 2: Toolbar (tabs, search, filters, sort) */}
            {toolbar && (
                <div className="pt-4">
                    {toolbar}
                </div>
            )}

            {/* Additional content */}
            {children && (
                <div className="pt-4">
                    {children}
                </div>
            )}
        </div>
    )
}
